import { type Dtos, type SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../utils/request";
import { incomeStatementsQueryKey } from "./financials.keys";
import { patchIncomeStatementView } from "./utils/patchIncomeStatementView";

export function useCreateIncomeStatementLineItem(accountId: SuretyAccountId) {
	const { post } = useRequest();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationKey: ["createIncomeStatementLineItem", accountId],
		mutationFn: (body: Dtos.Admin.IncomeStatement.CreateLineItem.Request) =>
			post(`/v2/admin/surety/accounts/${accountId}/income-statements/line-items`, body, "none"),
		onMutate: async (body) => {
			await queryClient.cancelQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			const previousData = queryClient.getQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
			);
			const periodByIncomeStatementId = new Map(
				body.periods.map((period) => [period.incomeStatementId, period]),
			);
			queryClient.setQueryData<Dtos.Admin.IncomeStatement.List.Response>(
				incomeStatementsQueryKey(accountId),
				(cached) =>
					cached?.map((statement) => {
						const period = periodByIncomeStatementId.get(statement.id);
						return period !== undefined && statement.view
							? {
									...statement,
									view: patchIncomeStatementView(statement.view, {
										field: body.field,
										label: body.label,
										values: { value: period.value, formula: period.formula },
									}),
								}
							: statement;
					}),
			);
			return { previousData };
		},
		onError: (_error, _vars, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(incomeStatementsQueryKey(accountId), context.previousData);
			}
		},
		onSettled: () => {
			if (
				queryClient.isMutating({ mutationKey: ["createIncomeStatementLineItem", accountId] }) <= 1
			) {
				queryClient.invalidateQueries({ queryKey: incomeStatementsQueryKey(accountId) });
			}
		},
	});

	return {
		createIncomeStatementLineItem: mutation.mutateAsync,
	};
}
